import { DEBOUNCE_TIME } from 'common/utilities/timeout/timeout';
import debounce from 'debounce';
import type React from 'react';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';

type ScrollingAxis = 'x' | 'y';

interface ScrollPosition {
  x: number;
  y: number;
  scrollingAxis?: ScrollingAxis;
}

export const useScroll = (
  scrollableRef?: React.RefObject<HTMLDivElement> | null, live?: boolean,
): ScrollPosition => {
  const scrollableEl = useRef<HTMLDivElement | undefined>();

  const getScrollX = () => (
    scrollableEl?.current?.scrollLeft === undefined ? window.scrollX : scrollableEl?.current?.scrollLeft
  );

  const getScrollY = () => (
    scrollableEl?.current?.scrollTop === undefined ? window.scrollY : scrollableEl?.current?.scrollTop
  );

  /** Previous scroll position (live, not debounced) */
  const previousScroll = useRef<ScrollPosition>({ x: 0, y: 0 });
  /** Debounced scroll position */
  const [scroll, setScrollState] = useState<ScrollPosition>({ x: 0, y: 0 });

  const setScroll = (newScrollX: number, newScrollY: number) => {
    setScrollState({
      x: newScrollX,
      y: newScrollY,
      scrollingAxis: undefined,
    });
    previousScroll.current = { x: newScrollX, y: newScrollY, scrollingAxis: undefined };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetScroll = useCallback(
    debounce(setScroll, live === true ? 0 : DEBOUNCE_TIME.scroll),
    [],
  );

  const handleScroll = useCallback(() => {
    let newScrollingAxis: ScrollingAxis | undefined;

    // When scrolling
    if (previousScroll.current.x !== getScrollX() || previousScroll.current.y !== getScrollY()) {
      newScrollingAxis = previousScroll.current.x !== getScrollX() ? 'x' : 'y';

      // If the user start scrolling a different axis, immediate callback (onStart)
      if (previousScroll.current.scrollingAxis !== newScrollingAxis) {
        setScrollState((prevState) => ({ ...prevState, scrollingAxis: newScrollingAxis }));
      }
    }

    previousScroll.current = { x: getScrollX(), y: getScrollY(), scrollingAxis: newScrollingAxis };

    debouncedSetScroll(getScrollX(), getScrollY());
  }, [debouncedSetScroll]);

  useEffect(() => {
    if (scrollableRef?.current && !scrollableEl.current) {
      scrollableEl.current = scrollableRef.current;
      setScroll(getScrollX(), getScrollY());
      scrollableEl.current.addEventListener('scroll', handleScroll);
    }
  }, [handleScroll, scrollableRef]);

  useEffect(() => {
    if (!scrollableEl?.current) {
      setScroll(window.scrollX, window.scrollY);
      window.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (!scrollableEl?.current) window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll, scrollableEl]);

  return scroll;
};

export default useScroll;
